import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { RealtimeSpaceService } from 'src/app/services/realtime-space.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { SubjectMap } from 'src/app/sessions/common/session-data-structures';
import { SpaceRepository } from 'src/app/state/space.repository';
import { modifiedInterval, modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { PanelView } from '../../panel/panel.component';
import {
  ParticipantsManagerEvent,
  Section,
} from '../../panel/participants-manager/participants-manager.component';
import { EventCategory, SessionEvent } from '../SessionEvent';
import { RaiseHandRepositoryService } from './raise-hand-repository.service';

export const LOWER_ALL_KEY = 'lowerAll';

@UntilDestroy()
@Injectable()
export class RaiseHandUiService {
  private lowerHandSentSubjectMap = new SubjectMap<boolean>();
  private readonly lowerHandSentPendingMemo = new Map<string, Observable<boolean>>();
  private lowerHandSentTimeouts = new Map<string, NodeJS.Timer>();
  private raisedHandAtMs = new Map<string, number>();

  private _hostQueueRaisedHandSubtitles$ = new BehaviorSubject<Map<string, string>>(new Map());
  public hostQueueRaisedHandSubtitles$ = this._hostQueueRaisedHandSubtitles$.pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
    untilDestroyed(this),
  );

  private hostQueueSubtitlesRefreshTrigger$ = new Subject<void>();

  public isRaiseHandQueueEnabled$ = combineLatest([
    this.spaceRepository.isCurrentUserHost$.pipe(distinctUntilChanged()),
    this.providerState.callConnected$.pipe(distinctUntilChanged()),
  ]).pipe(
    map(([isHost, connected]) => isHost && connected),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
    untilDestroyed(this),
  );

  public raisedHandQueueCount$: Observable<number> = combineLatest([
    this.raiseHandRepository.orderedRaisedHandUserIds$,
    this.isRaiseHandQueueEnabled$,
  ]).pipe(
    map(([ids, enabled]) => (enabled ? ids.length : 0)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
    untilDestroyed(this),
  );

  private queuePanelShown$ = combineLatest([
    this.sessionSharedDataService.rightPanelView,
    this.sessionSharedDataService.changeParticipantsManagerSection,
  ]).pipe(
    map(
      ([right, section]) =>
        right?.panelView === PanelView.participantsManager &&
        !!right?.expanded &&
        section === Section.QUEUE,
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
    untilDestroyed(this),
  );

  constructor(
    private raiseHandRepository: RaiseHandRepositoryService,
    private spaceRepository: SpaceRepository,
    private realtimeSpaceService: RealtimeSpaceService,
    private providerState: ProviderStateService,
    private translateService: TranslateService,
    private sessionSharedDataService: SessionSharedDataService,
  ) {
    this.setRaiseHandchangesSubscriptions();
    this.setEmptyQueueSubscription();
    this.setRoomResetSubscription();
    this.setQueuePanelSubscription();
    this.setHostQueueSubtitlesSubscription();
  }

  lowerHandSentPending$(key: string): Observable<boolean> {
    let cachedObservable = this.lowerHandSentPendingMemo.get(key);
    if (!cachedObservable) {
      cachedObservable = this.lowerHandSentSubjectMap.get(key).pipe(
        map((v) => !!v),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
        untilDestroyed(this),
      );
      this.lowerHandSentPendingMemo.set(key, cachedObservable);
    }
    return cachedObservable;
  }

  lowerAllHandSentPending$(): Observable<boolean> {
    return this.lowerHandSentPending$(LOWER_ALL_KEY);
  }

  lowerRaisedHandForUser(userId: string): void {
    if (!this.handleLowerHand(userId)) {
      return;
    }
    const event = new SessionEvent(
      EventCategory.ParticipantsManager,
      ParticipantsManagerEvent.LOWER_HAND,
      { userId },
    );
    this.realtimeSpaceService.service.sendEvent(this, event);
  }

  lowerAllRaisedHands(): void {
    if (!this.handleLowerHand(LOWER_ALL_KEY)) {
      return;
    }
    const event = new SessionEvent(
      EventCategory.ParticipantsManager,
      ParticipantsManagerEvent.LOWER_HAND_ALL,
      {},
    );
    this.realtimeSpaceService.service.sendEvent(this, event);
  }

  private setRaiseHandchangesSubscriptions(): void {
    this.raiseHandRepository.newlyRaisedHandUserIds$
      .pipe(untilDestroyed(this))
      .subscribe((userIds) => {
        this.onHandsRaised(userIds);
      });

    this.raiseHandRepository.newlyLoweredHandUserIds$
      .pipe(untilDestroyed(this))
      .subscribe((userIds) => {
        this.onHandsLowered(userIds);
      });
  }

  private setEmptyQueueSubscription(): void {
    this.raiseHandRepository.orderedRaisedHandUserIds$
      .pipe(
        filter((ids) => ids.length === 0),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.clearLowerAllPending();
      });
  }

  private setRoomResetSubscription(): void {
    this.spaceRepository.activeSpaceCurrentRoomUid$
      .pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => {
        this.reset();
      });
  }

  private reset(): void {
    this.resetLowerHandSentTimers();
    this.raisedHandAtMs.clear();
    this._hostQueueRaisedHandSubtitles$.next(new Map());
  }

  private onHandsRaised(userIds: string[]): void {
    const now = Date.now();
    for (const id of userIds) {
      this.raisedHandAtMs.set(id, now);
    }
  }

  private onHandsLowered(userIds: string[]): void {
    for (const userId of userIds) {
      this.raisedHandAtMs.delete(userId);
      this.clearLowerHandSent(userId);
    }
  }

  private clearLowerAllPending(): void {
    this.clearLowerHandSent(LOWER_ALL_KEY);
  }

  private setQueuePanelSubscription(): void {
    this.queuePanelShown$
      .pipe(
        switchMap((queuePanelShown) => {
          if (!queuePanelShown) {
            return EMPTY;
          }

          this.hostQueueSubtitlesRefreshTrigger$.next();
          return modifiedInterval(30000);
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.hostQueueSubtitlesRefreshTrigger$.next();
      });
  }

  private setHostQueueSubtitlesSubscription(): void {
    combineLatest([
      this.raiseHandRepository.orderedRaisedHandUserIds$,
      this.hostQueueSubtitlesRefreshTrigger$.pipe(startWith(undefined)),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([orderedIds]) => {
        this.emitHostQueueSubtitles(orderedIds);
      });
  }

  private emitHostQueueSubtitles(orderedIds: string[]): void {
    if (!orderedIds.length) {
      this._hostQueueRaisedHandSubtitles$.next(new Map());
      return;
    }

    this._hostQueueRaisedHandSubtitles$.next(this.buildRaisedHandQueueSubtitles(orderedIds));
  }

  private buildRaisedHandQueueSubtitles(orderedIds: string[]): Map<string, string> {
    const subtitles = new Map<string, string>();
    for (const id of orderedIds) {
      subtitles.set(id, this.getRaisedHandAgeLabel(id));
    }
    return subtitles;
  }

  private getRaisedHandAgeLabel(userId: string): string {
    const defaultLabel = this.translateService.instant('Just now');

    const t = this.raisedHandAtMs.get(userId);
    if (t === undefined) {
      return defaultLabel;
    }
    const sec = Math.floor((Date.now() - t) / 1000);
    if (sec < 60) {
      return defaultLabel;
    }
    const totalMin = Math.floor(sec / 60);
    if (totalMin < 60) {
      return `${totalMin}m`;
    }
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m === 0 ? `${h}h` : `${h}h-${m}m`;
  }

  private clearLowerHandSent(key: string): void {
    const timer = this.lowerHandSentTimeouts.get(key);
    if (timer) {
      clearTimeout(timer);
      this.lowerHandSentTimeouts.delete(key);
    }
    this.lowerHandSentSubjectMap.get(key).next(false);
  }

  private handleLowerHand(key: string): boolean {
    if (this.lowerHandSentSubjectMap.get(key).getValue()) {
      return false;
    }

    // defensive check not to leak timers
    const existingTimer = this.lowerHandSentTimeouts.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    this.lowerHandSentSubjectMap.get(key).next(true);
    const timer = this.createLowerHandTimer(key);
    this.lowerHandSentTimeouts.set(key, timer);
    return true;
  }

  private createLowerHandTimer(key: string): NodeJS.Timer {
    return modifiedSetTimeout(() => {
      this.lowerHandSentTimeouts.delete(key);
      this.lowerHandSentSubjectMap.get(key).next(false);
    }, 5000);
  }

  private resetLowerHandSentTimers(): void {
    for (const timer of this.lowerHandSentTimeouts.values()) {
      clearTimeout(timer);
    }
    this.lowerHandSentTimeouts.clear();
    this.lowerHandSentSubjectMap.clear();
    this.lowerHandSentPendingMemo.clear();
  }
}
